import * as XLSX from "xlsx";
import {
  type Column,
  applyAutoFilter,
  autoColumnWidths,
  headerLabel,
  safeSheetName,
} from "@racoon/ui";
import { downloadBlob } from "@racoon/lib";
import { formatDateToISO } from "~/shared/helpers";

export interface ExportConfig<T> {
  data: T[];
  columns: Column<T>[];
  filename: string;
  sheetName?: string;
  additionalColumns?: { header: string; accessor: (row: T) => any }[];
}

// The print document is written as HTML into a same-origin iframe, so every value is escaped.
const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

export interface PrintConfig<T> {
  data: T[];
  columns: Column<T>[];
  title: string;
  /** The whole period line, already localized (e.g. common.pdfPeriod). */
  period?: string;
  filterInfo?: string;
  additionalColumns?: { header: string; accessor: (row: T) => any }[];
}

/**
 * Экспорт данных в Excel
 */
export function exportToExcel<T>(config: ExportConfig<T>): void {
  try {
    const { data, columns, filename, sheetName = "Sheet1", additionalColumns = [] } = config;

    // Collect headers
    const headers = [...columns.map(headerLabel), ...additionalColumns.map((col) => col.header)];

    // Extract value from column
    const getCellValue = (row: T, column: Column<T>, index: number): unknown => {
      if (column.accessor && typeof column.accessor === "function") {
        return column.accessor(row, index);
      }
      if (column.key) {
        return (row as any)[column.key];
      }
      return "";
    };

    // Collect data
    const exportData = data.map((row, index) => [
      ...columns.map((col) => getCellValue(row, col, index)),
      ...additionalColumns.map((col) => col.accessor(row)),
    ]);

    // Create Excel sheet
    const ws = XLSX.utils.aoa_to_sheet([headers, ...exportData]);
    ws["!cols"] = autoColumnWidths(headers, exportData);
    applyAutoFilter(ws);
    const wb = XLSX.utils.book_new();
    // A report name over 31 chars (or containing []:*?/\) makes book_append_sheet
    // throw, which reaches the user as a failed export.
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheetName));

    // Download file
    XLSX.writeFile(wb, filename);
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    throw new Error("Ошибка экспорта в Excel");
  }
}

/**
 * Печать таблицы
 */
export function printTable<T>(config: PrintConfig<T>): void {
  try {
    const { data, columns, title, period, filterInfo, additionalColumns = [] } = config;

    // Extract value from column for printing; a zero prints, a missing value doesn't.
    const printable = (value: unknown) => (value == null || value === false ? "" : String(value));
    const getCellValue = (row: T, column: Column<T>, index: number): string => {
      if (column.accessor && typeof column.accessor === "function") {
        return printable(column.accessor(row, index));
      }
      if (column.key) {
        return printable((row as Record<string, unknown>)[column.key]);
      }
      return "";
    };

    // Form HTML for printing
    const printContent = `
      <html>
        <head>
          <title>${escapeHtml(title)}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              font-size: 12px;
            }
            table { 
              border-collapse: collapse; 
              width: 100%; 
              font-size: 10px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 4px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
            }
            h1 { 
              color: #333; 
              font-size: 18px;
              margin-bottom: 10px;
            }
            .info { 
              margin: 10px 0; 
              font-style: italic; 
              font-size: 11px;
            }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(title)}</h1>
          ${period ? `<div class="info">${escapeHtml(period)}</div>` : ""}
          ${filterInfo ? `<div class="info">${escapeHtml(filterInfo)}</div>` : ""}
          <table>
            <thead>
              <tr>
                ${columns.map((col) => `<th>${escapeHtml(headerLabel(col))}</th>`).join("")}
                ${additionalColumns.map((col) => `<th>${escapeHtml(col.header)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${data
                .map(
                  (row, index) => `
                <tr>
                  ${columns.map((col) => `<td>${escapeHtml(getCellValue(row, col, index))}</td>`).join("")}
                  ${additionalColumns.map((col) => `<td>${escapeHtml(printable(col.accessor(row)))}</td>`).join("")}
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Create hidden iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";

    document.body.appendChild(iframe);

    // Write content and print
    iframe.contentDocument?.open();
    iframe.contentDocument?.write(printContent);
    iframe.contentDocument?.close();

    iframe.contentWindow?.focus();
    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 500);
    }, 200);
  } catch (error) {
    console.error("Error printing table:", error);
    throw new Error("Ошибка печати таблицы");
  }
}

export { downloadBlob };

// Prints via a hidden iframe (same technique as printTable) so the printed
// page matches what downloadBlob would have saved, byte for byte.
export function printBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.style.position = "absolute";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  iframe.src = url;

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    document.body.removeChild(iframe);
    URL.revokeObjectURL(url);
  };

  document.body.appendChild(iframe);
  iframe.onload = () => {
    const win = iframe.contentWindow;
    // Cleans up once the print dialog actually closes, instead of guessing how
    // long the user takes; the timeout below is only a fallback for browsers
    // that never fire it.
    win?.addEventListener("afterprint", cleanup);
    win?.focus();
    win?.print();
  };

  setTimeout(cleanup, 5 * 60_000);
}

/**
 * Форматирование даты для файлов
 */
function formatDateForFilename(date: Date | string): string {
  if (typeof date === "string") {
    return date.replace(/[^\d]/g, "-");
  }
  return formatDateToISO(date as Date);
}

/**
 * Генерация имени файла для экспорта
 */
export function generateFilename(
  prefix: string,
  startDate?: string | Date,
  endDate?: string | Date,
  extension: string = ".xlsx",
): string {
  const parts = [prefix];

  if (startDate) {
    parts.push(formatDateForFilename(startDate));
  }

  if (endDate && endDate !== startDate) {
    parts.push(formatDateForFilename(endDate));
  }

  return parts.join("_") + extension;
}
